import { PlusIcon } from '@chegg-ui/horizon-icons'
import { colors } from '@chegg-ui/theme'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { ONE_AUTH_MODAL } from '@chegg-wt/core/state/modals/constants'
import {
  AuthModalData,
  AuthModalOrigin,
} from '@chegg-wt/core/components/Modals/AuthModal/types'

import { useDispatch, useSelector } from 'react-redux'
import {
  notifyToast,
  setPaperList,
  setRequestForNewPaper,
} from '@chegg-wt/core-wa/state/actions'
import { FC } from 'react'
import {
  paperIdSelector,
  paperListSelector,
  paperTitleSelector,
  wordCountSelector,
} from '@chegg-wt/core-wa/state/selectors'
import { EMPTY_PAPER } from '@chegg-wt/core-wa/state/constants'
import { navigated } from '@chegg-wt/core/state/app/actions'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import {
  getCurrentDateInMMDDYY,
  resetPaperAndEditor,
} from '@chegg-wt/core-wa/utils/helpers'
import { createPaper } from '@chegg-wt/core-wa/utils/paperIndexedDb'
import { Editor } from '@tiptap/react'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { ToastType } from '@chegg-wt/core-wa/state/types'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import {
  ItemContainer,
  NewDocButtonContainer,
  NewDocumentButtonSubtitle,
  NewDocumentButtonTitle,
} from '../styled'
import { onCloseQARightPanel } from '../../QuickActionRightPanel/functions/utils'

const authModalData: AuthModalData = {
  defaultFlow: 'signup',
  origin: AuthModalOrigin.WRITING_ASSISTANT_PAGE,
}

interface NewDocumentButtonProps {
  isLoggedIn: boolean
  isPaperCheckInProgress: boolean
}

export const NewDocumentButton: FC<NewDocumentButtonProps> = ({
  isLoggedIn,
  isPaperCheckInProgress,
}) => {
  const dispatch = useDispatch()
  const paperList = useSelector(paperListSelector)
  const { toWritingAssistant } = useRoutesContext()
  const paperId = useSelector(paperIdSelector)
  const title = useSelector(paperTitleSelector)
  const wordCount = useSelector(wordCountSelector)

  const isCurrrentPaperIsInDraft = () => {
    if (wordCount === 0 && !title && !paperId) {
      return true
    }
    return false
  }

  const handleClick = async () => {
    // TODO: create common functions, optimize code for better readability.
    if (isPaperCheckInProgress) return
    dispatch(setRequestForNewPaper(true))

    if (isLoggedIn) {
      if (isCurrrentPaperIsInDraft()) {
        dispatch(notifyToast(ToastType.DRAFT_PAPER_EXISTS))
        return
      }
      onCloseQARightPanel(dispatch)

      const updatedPaperList = [...paperList]

      if (!paperId && (title || wordCount > 0)) {
        let newTitle = title

        if (!title) {
          newTitle = `Paper - ${getCurrentDateInMMDDYY()}`
        }

        const editor: Editor = getFeatureEditor()
        const paperContent = {
          editorJSON: editor.getJSON(),
        }

        const newPaperId = await createPaper(
          newTitle,
          paperContent,
          {
            feedbackList: [],
            sentences: [],
            totalFeedbackCount: 0,
          },
          isLoggedIn
        )

        const updatedPaper = {
          ...updatedPaperList[0],
          title: newTitle,
          paperId: newPaperId,
        }
        updatedPaperList[0] = updatedPaper
        dispatch(setPaperList([...updatedPaperList]))
      }
      dispatch(navigated(toWritingAssistant()))
      resetPaperAndEditor(dispatch)

      // Add a fresh paper to the List with null as paperId
      const newPapersList = [EMPTY_PAPER, ...updatedPaperList]
      dispatch(setPaperList(newPapersList))
    } else {
      // Guest experience
      dispatch(openModal({ name: ONE_AUTH_MODAL, data: authModalData }))
    }
    dispatch(
      userClick({
        origin: origins.startNewPaperClick,
      })
    )
  }

  return (
    <NewDocButtonContainer
      onClick={handleClick}
      isPaperCheckInProgress={isPaperCheckInProgress}
    >
      <ItemContainer>
        <NewDocumentButtonTitle>New document</NewDocumentButtonTitle>
        <NewDocumentButtonSubtitle>Start a new draft</NewDocumentButtonSubtitle>
      </ItemContainer>
      <PlusIcon size="xsmall" color={colors.grey800} />
    </NewDocButtonContainer>
  )
}
